import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import Papa from "papaparse";
import type { WorkSession } from "@/types/workSession.type";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Fonction utilitaire pour obtenir les initiales d'un nom
 * @param firstName - Le prénom (ex: "Jean")
 * @param lastName - Le nom de famille (ex: "Dupont")
 * @param name - Le nom complet (optionnel, pour compatibilité avec l'ancien format)
 * @returns Les initiales en majuscules (ex: "JD")
 * @example
 * getInitials("Jean", "Dupont") // "JD"
 * getInitials("Marie", undefined) // "M"
 * getInitials(undefined, undefined, "Jean Dupont") // "JD" (ancien format)
 */
export function getInitials(
  firstName?: string,
  lastName?: string,
  name?: string
): string {
  // Si firstName et lastName sont fournis, les utiliser
  if (firstName || lastName) {
    const initials: string[] = [];
    if (firstName) initials.push(firstName[0]);
    if (lastName) initials.push(lastName[0]);
    return initials.join("").toUpperCase().slice(0, 2);
  }

  // Sinon, utiliser l'ancien format avec name (pour compatibilité)
  if (name) {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }

  // Par défaut, retourner "U" pour "User"
  return "U";
}

/**
 * Formate une durée en heures au format "04h03" (comme l'affichage dans le backoffice)
 * @param hours - Durée en heures (ex: 4.05 pour 4 heures et 3 minutes)
 * @returns Durée formatée (ex: "04h03") ou "-" si la durée est 0 ou nulle
 * @example
 * formatDurationForCSV(4.05) // "04h03"
 * formatDurationForCSV(0.5) // "00h30"
 * formatDurationForCSV(8.25) // "08h15"
 */
export function formatDurationForCSV(hours: number | null | undefined): string {
  // Si la durée est nulle, 0 ou non définie, retourner "N/A"
  if (!hours || hours === 0) return "N/A";

  // Convertir les heures en minutes totales
  // Exemple: 4.05 heures = 4.05 * 60 = 243 minutes
  const totalMinutes = Math.round(hours * 60);

  // Calculer les heures et minutes
  // Exemple: 243 minutes = 4 heures et 3 minutes
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  // Formater avec des zéros devant si nécessaire (ex: "04h03")
  // padStart(2, '0') ajoute un zéro devant si le nombre est < 10
  return `${h.toString().padStart(2, "0")}h${m.toString().padStart(2, "0")}`;
}

/**
 * Convertit un tableau de sessions en format CSV pour PapaParse
 * @param sessions - Tableau de sessions à convertir
 * @returns Tableau d'objets avec les colonnes du CSV
 */
export function formatSessionsForCSV(sessions: WorkSession[]) {
  return sessions.map((session) => {
    // Formater la date (ex: "22/01/2024")
    const date = format(new Date(session.date), "dd/MM/yyyy", { locale: fr });

    // Nom complet de l'employé
    const employeeName = session.user
      ? `${session.user.firstName || ""} ${session.user.lastName || ""}`.trim()
      : "N/A";

    // Nom de l'entreprise
    const companyName = session.company?.name || "N/A";

    // Heure de début (ex: "08:30")
    const startTime = session.startTime
      ? format(new Date(session.startTime), "HH:mm")
      : "N/A";

    // Heure de fin (ex: "17:30" ou "En cours")
    const endTime = session.endTime
      ? format(new Date(session.endTime), "HH:mm")
      : "En cours";

    // Durée formatée comme à l'affichage (ex: "04h03" au lieu de "4.05")
    // Utilise la nouvelle fonction formatDurationForCSV
    const duration = formatDurationForCSV(session.duration);

    // Statut (En cours ou Terminée)
    const status = session.endTime ? "Terminée" : "En cours";

    // Retourner un objet avec les colonnes du CSV
    // Les clés deviennent les en-têtes du CSV
    return {
      Date: date,
      Employé: employeeName,
      Entreprise: companyName,
      "Heure de début": startTime,
      "Heure de fin": endTime,
      Durée: duration, // Changé de "Durée (heures)" à "Durée" pour plus de clarté
      Statut: status,
    };
  });
}

/**
 * Convertit un tableau d'heures par employé en format CSV pour PapaParse
 */
export function formatEmployeeHoursForCSV(
  employees: { employee: { name: string }; minutes: number; sessionsCount: number }[],
  label: string
) {
  return employees.map((row) => {
    const h = Math.floor(row.minutes / 60);
    const m = row.minutes % 60;
    const totalHeures = row.minutes > 0
      ? `${h}h ${String(m).padStart(2, "0")}m`
      : "0h 00m";

    return {
      Employé: row.employee.name,
      Sessions: row.sessionsCount,
      "Total heures": totalHeures,
      Période: label,
    };
  });
}

/**
 * Télécharge un fichier CSV en utilisant PapaParse
 * @param data - Tableau d'objets à convertir en CSV
 * @param filename - Nom du fichier (ex: "sessions_2024-01-22.csv")
 */
export function downloadCSV(
  data: Record<string, string | number>[],
  filename: string
): void {
  // Convertir les données en CSV avec PapaParse
  const csv = Papa.unparse(data, {
    delimiter: ",",
    newline: "\n",
    header: true, // Inclure les en-têtes
    quotes: true, // Encapsuler les valeurs avec des guillemets si nécessaire
  });

  // Créer un Blob avec le contenu CSV
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

  // Créer un lien temporaire pour télécharger le fichier
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  // Configurer le lien
  link.href = url;
  link.download = filename;

  // Ajouter le lien au DOM (nécessaire pour certains navigateurs)
  document.body.appendChild(link);

  // Simuler un clic pour déclencher le téléchargement
  link.click();

  // Nettoyer : retirer le lien et libérer l'URL
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
